import { useState, useEffect } from 'react';

/**
 * Custom hook for countdown timer
 * @param targetTime - The target date/time to count down to
 * @param onComplete - Optional callback to execute when countdown reaches 0
 * @returns Time remaining in seconds
 */
export function useCountdown(targetTime: Date | null, onComplete?: () => void): number {
  const [timeRemaining, setTimeRemaining] = useState(0);
  
  useEffect(() => {
    // Return 0 when target is null or in the past
    if (!targetTime) {
      setTimeRemaining(0);
      return;
    }

    // Calculate initial time remaining
    const calculateTimeRemaining = () => {
      const now = new Date().getTime();
      const target = targetTime.getTime();
      const diff = target - now;
      return diff > 0 ? Math.floor(diff / 1000) : 0;
    };

    // Set initial value
    const initial = calculateTimeRemaining();
    setTimeRemaining(initial);
    
    // If already at 0, trigger callback immediately
    if (initial === 0 && onComplete) {
      onComplete();
    }

    // Update every second using setInterval
    const intervalId = setInterval(() => {
      const remaining = calculateTimeRemaining();
      setTimeRemaining(remaining);
      
      // Stop interval when countdown reaches 0
      if (remaining <= 0) {
        clearInterval(intervalId);
        // Trigger callback when countdown completes
        if (onComplete) {
          onComplete();
        }
      }
    }, 1000);

    // Clean up interval on unmount or when targetTime changes
    return () => {
      clearInterval(intervalId);
    };
  }, [targetTime, onComplete]);
  
  return timeRemaining;
}
